/**
 * Invoice emails over Gmail SMTP (decisions-log E1–E4, V283): one account per client, a subject
 * and body template with `{placeholders}`, and a record of every send attempt. Email content is
 * invoice content, so it is English like the PDF (formatTemplateDate, dot decimals).
 */
import { z } from 'zod';
import { formatTemplateDate } from '../dates';

export const EMAIL_PROVIDER = { host: 'smtp.gmail.com', port: 465, secure: true } as const;

/** Google issues app passwords as 16 letters, usually shown in groups of four. */
export const APP_PASSWORD_LENGTH = 16;
export const MAX_RECIPIENTS = 10;
export const MAX_SUBJECT_LENGTH = 200;
export const MAX_BODY_LENGTH = 5000;

export const EMAIL_PLACEHOLDERS = [
  'number',
  'client',
  'contact',
  'issuer',
  'period',
  'date',
  'due',
  'total',
  'currency',
] as const;
export type EmailPlaceholder = (typeof EMAIL_PLACEHOLDERS)[number];
export type EmailVars = Readonly<Record<EmailPlaceholder, string>>;

export const DEFAULT_SUBJECT_TEMPLATE = 'Invoice {number} from {issuer}';
export const DEFAULT_BODY_TEMPLATE = [
  'Hello {contact},',
  '',
  'Please find attached invoice {number} for {period}.',
  'Amount due: {total} {currency}, payable by {due}.',
  '',
  'Best regards,',
  '{issuer}',
].join('\n');

export const EMAIL_MESSAGES = {
  email: 'Введите адрес электронной почты, например name@example.com',
  appPassword: 'Введите 16-значный пароль приложения из аккаунта Google',
  subject: 'Введите тему',
  body: 'Введите текст письма',
  recipients: 'Добавьте хотя бы одного получателя',
  tooManyRecipients: `Не более ${MAX_RECIPIENTS} получателей`,
  noAccount: 'Сначала настройте отправку: Клиенты → карточка → «Отправка счетов»',
  notIssued: 'Выставите счёт, прежде чем отправлять его по почте',
  cancelled: 'Аннулированные счета нельзя отправить по почте',
  clientInvoice: 'Счета клиента по почте не отправляются',
  smtpFailed: 'Gmail не принял письмо',
  loginFailed: 'Gmail отклонил адрес или пароль приложения',
} as const;

const emailSchema = z.string().trim().toLowerCase().email(EMAIL_MESSAGES.email);
const recipientsSchema = z
  .array(emailSchema)
  .min(1, EMAIL_MESSAGES.recipients)
  .max(MAX_RECIPIENTS, EMAIL_MESSAGES.tooManyRecipients);
const ccSchema = z.array(emailSchema).max(MAX_RECIPIENTS, EMAIL_MESSAGES.tooManyRecipients).default([]);
const subjectSchema = z.string().trim().min(1, EMAIL_MESSAGES.subject).max(MAX_SUBJECT_LENGTH);
const bodySchema = z.string().trim().min(1, EMAIL_MESSAGES.body).max(MAX_BODY_LENGTH);

/** Spaces that Google inserts between the four-letter groups are dropped. */
export const normaliseAppPassword = (value: string): string => value.replace(/\s+/g, '');
const appPasswordSchema = z
  .string()
  .transform(normaliseAppPassword)
  .refine((value) => value.length === APP_PASSWORD_LENGTH, EMAIL_MESSAGES.appPassword);

/**
 * PUT /clients/:id/email-account — the password may be omitted when one is already stored, or when
 * the address is a mailbox another visible client saved with its password (V291).
 */
export const emailAccountUpsertSchema = z.object({
  fromEmail: emailSchema,
  appPassword: appPasswordSchema.optional(),
  replyTo: emailSchema.nullable().optional(),
  subjectTemplate: subjectSchema,
  bodyTemplate: bodySchema,
});
export type EmailAccountUpsert = z.infer<typeof emailAccountUpsertSchema>;

/** POST /clients/:id/email-account/verify — checks a login without saving. */
export const emailAccountVerifySchema = z.object({
  fromEmail: emailSchema,
  /** Omitted → the stored password. */
  appPassword: appPasswordSchema.optional(),
});
export type EmailAccountVerify = z.infer<typeof emailAccountVerifySchema>;

/** POST /invoices/:id/email */
export const invoiceEmailSendSchema = z.object({
  to: recipientsSchema,
  cc: ccSchema,
  subject: subjectSchema,
  body: bodySchema,
});
export type InvoiceEmailSend = z.infer<typeof invoiceEmailSendSchema>;

// ------------------------------------------------------------------ read models

/** The stored account without its password. */
export interface EmailAccountItem {
  readonly clientId: string;
  readonly fromEmail: string;
  readonly hasPassword: boolean;
  readonly replyTo: string | null;
  readonly subjectTemplate: string;
  readonly bodyTemplate: string;
  readonly lastVerifiedAt: string | null;
  readonly updatedAt: string;
}

export interface EmailAccountResponse {
  readonly item: EmailAccountItem | null;
}

/**
 * GET /clients/:id/email-account/mailboxes — a Gmail address already saved with its password under
 * a client the person can see (V291); the password is reused when this client picks the address.
 */
export interface EmailMailbox {
  readonly fromEmail: string;
  /** Names of the other clients whose account uses this address (the requesting client left out). */
  readonly sharedWith: readonly string[];
  readonly lastVerifiedAt: string | null;
}

export interface EmailMailboxesResponse {
  readonly items: readonly EmailMailbox[];
}

/** GET /invoices/:id/email-draft */
export interface InvoiceEmailDraft {
  readonly invoiceId: string;
  /** Null when the client has no email account yet. */
  readonly fromEmail: string | null;
  readonly to: readonly string[];
  readonly cc: readonly string[];
  /** Every known contact email of the client, for suggestions. */
  readonly knownRecipients: readonly string[];
  readonly subject: string;
  readonly body: string;
  readonly attachmentName: string;
  /** Previous sends of this invoice, newest first. */
  readonly history: readonly InvoiceEmailItem[];
}

export const INVOICE_EMAIL_STATUSES = ['sent', 'failed'] as const;
export type InvoiceEmailStatus = (typeof INVOICE_EMAIL_STATUSES)[number];

/** `email`: sent by the app over Gmail; `other`: sent outside the app and recorded by hand (V293). */
export const INVOICE_SEND_CHANNELS = ['email', 'other'] as const;
export type InvoiceSendChannel = (typeof INVOICE_SEND_CHANNELS)[number];

/** How a send outside the app went («через портал клиента»); V293. */
export const MAX_SEND_NOTE_LENGTH = 200;

/** One send attempt of an invoice. A send outside the app has no sender, recipients or subject. */
export interface InvoiceEmailItem {
  readonly id: string;
  readonly invoiceId: string;
  readonly channel: InvoiceSendChannel;
  readonly fromEmail: string | null;
  readonly to: readonly string[];
  readonly cc: readonly string[];
  readonly subject: string | null;
  readonly attachmentName: string;
  readonly note: string | null;
  readonly status: InvoiceEmailStatus;
  readonly messageId: string | null;
  readonly error: string | null;
  readonly sentAt: string;
}

export const INVOICE_EMAIL_SKIP_REASONS = [
  'not_issued',
  'cancelled',
  'client_invoice',
  'no_account',
  'no_recipient',
  /** A payroll statement (M5) is never emailed. */
  'payroll_statement',
  /** Imported history (M2) has no PDF to send. */
  'imported_invoice',
] as const;
export type InvoiceEmailSkipReason = (typeof INVOICE_EMAIL_SKIP_REASONS)[number];

// ------------------------------------------------------------------ templates

/** Replaces `{placeholder}` tokens; unknown tokens stay as typed. */
export function renderEmailTemplate(template: string, vars: EmailVars): string {
  return template.replace(/\{(\w+)\}/g, (token, name: string) =>
    (EMAIL_PLACEHOLDERS as readonly string[]).includes(name) ? vars[name as EmailPlaceholder] : token,
  );
}

/** Period text as the PDF prints it: "01 Sep 2026 – 30 Sep 2026", or the single date. */
export function emailPeriodText(start: string | null, end: string | null): string {
  if (start && end) return `${formatTemplateDate(start)} – ${formatTemplateDate(end)}`;
  return formatTemplateDate(start ?? end ?? null);
}

/** English money for emails: 312843 → "3,128.43" (same as the PDF, never the screen format). */
export function emailMoney(minor: number): string {
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    minor / 100,
  );
}
